import { useState } from 'react';
import { visitEntities, type SmartFillBucket } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { usePaste } from '../sidebar/paste';
import { copyTextToClipboard, pluralize } from '../sidebar/presentation';
import { useSessionMemory } from '../sidebar/session-memory';
import { BucketTree } from './BucketTree';
import { ChevronRightIcon } from './Icons';

/**
 * Read-only view of the active bucket for quick reference, per-field copy,
 * and per-section paste while working a form. Review cards keep precedence
 * above; the whole browser collapses from its heading, and nested sections
 * start collapsed.
 */
export function BucketBrowser({ bucket }: { bucket: SmartFillBucket }) {
  const paste = usePaste();
  const { snapshot } = useSidebarSession();
  const selection = snapshot.pageSession.selection;
  const selectedId = selection?.bucketId === bucket.id ? selection.scopeEntityId : null;
  let selectedLabel: string | undefined;
  visitEntities(bucket.document.root, (entity) => {
    if (entity.id === selectedId) selectedLabel = entity.label ?? entity.type;
  });
  // The page view unmounts during fill phases (loading state), so browser
  // disclosure survives in session memory for the sidebar's lifetime.
  const [open, setOpen] = useSessionMemory(`bucket-browser:${bucket.id}`, () => true);
  const [pastingEntityId, setPastingEntityId] = useState<string | null>(null);
  const rootFieldCount = bucket.document.root.properties?.length ?? 0;

  // Scoped paste: the resolver narrows the source to this section's subtree,
  // so a repeated record (one driver among several) fills without ambiguity.
  const pasteSection = async (entityId: string, heading: string) => {
    setPastingEntityId(entityId);
    try {
      await paste.pasteOnce({
        scopeEntityId: entityId === bucket.document.root.id ? null : entityId,
        scopeLabel: heading
      });
    } finally {
      setPastingEntityId(null);
    }
  };

  return (
    <div className="bucket-browser">
      <div className="section-heading section-heading--bucket">
        <h2>
          <button
            type="button"
            className="entity-toggle"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            <ChevronRightIcon className="entity-toggle__chevron" />
            {bucket.name}
          </button>
        </h2>
        {!open && rootFieldCount > 0 && (
          <span className="entity-count">
            {rootFieldCount} {pluralize(rootFieldCount, 'field')}
          </span>
        )}
        {open && <span className="section-heading__hint">Click to copy a value</span>}
      </div>
      {selectedLabel && (
        <p className="section-heading__hint">
          This page was filled from {selectedLabel} only. The fill buttons use the whole client.
        </p>
      )}
      <BucketTree
        key={bucket.id}
        mode="browse"
        document={bucket.document}
        rootHeading={selectedId ? 'Whole client' : null}
        rootCollapsed={!open}
        stateKey={`browse:${bucket.id}`}
        onCopyValue={(property) => copyTextToClipboard(property.value)}
        onPasteSection={(entity, heading) => void pasteSection(entity.id, heading)}
        pasteDisabled={!paste.canPaste}
        pastingEntityId={pastingEntityId}
      />
    </div>
  );
}
